// Vendor
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { SyntheticEvent } from 'react';
// Bernie
import { AnalyticsStore, ContextStore, QueryRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
// UITK
import { UitkPageLoader } from 'uitk-react-loader';
// Stores
import { MultiItemStore } from 'stores/multi-item-store';
import { MultiItemStoreStatus } from 'stores/multi-item-store-util';
import { SearchCriteriaContext } from 'stores/contexts';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
// Models
import { InCartProperty } from 'models/multi-item/multi-item-types';
// Components
import { HoldingAreaContent } from 'components/multi-item/holding-area-content';
import { ItemsPlayback } from 'components/multi-item/items-playback';
import { UitkToast } from 'uitk-react-toast';

export interface MultiItemProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  multiItem?: MultiItemStore;
  routeStore?: RouteStore;
}

@withLocalization
@inject('multiItem', 'context', 'routeStore', 'analytics')
@observer
export class MultiItem extends React.Component<LocalizedComponentProps & MultiItemProps> {
  private toggleDialogRef;
  constructor(props) {
    super(props);

    this.cartItemDeleter = this.cartItemDeleter.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.getCartItems = this.getCartItems.bind(this);
    this.goToCheckout = this.goToCheckout.bind(this);
    this.onToastDismiss = this.onToastDismiss.bind(this);
    this.toggleDialogRef = React.createRef();
  }

  public componentDidMount() {
    this.getCartItems();
  }

  public componentDidUpdate() {
    const { multiItem, routeStore } = this.props;
    if (multiItem.status === MultiItemStoreStatus.ADDING_PROPERTY_SUCCESS) {
      if (routeStore && routeStore.queryParams && routeStore.queryParams.pwaDialogNested) {
        routeStore.goBack();
        // Give some time for the nested dialog to go away
        setTimeout(() => {
          this.toggleDialogRef.click();
        }, 10);
      } else {
        this.toggleDialogRef.click();
      }
      multiItem.resetStatus();
    }
  }

  public closeDialog() {
    const { routeStore } = this.props;

    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }
  }

  public goToCheckout() {
    const { l10n, multiItem, context, analytics } = this.props;
    let prepareCheckout = multiItem.prepareCheckoutByCartId.bind(multiItem);
    if (multiItem.cartContainsUnavailItems()) {
      const confirmationResponse = confirm(l10n.formatText('multiProperty.playback.propertiesInTripUnavailable'));
      if (confirmationResponse === true) {
        prepareCheckout = multiItem.removeUnavailableAndPrepareCheckout.bind(multiItem);
      } else {
        return;
      }
    }

    return prepareCheckout(context, analytics)
      .then(prepareCheckoutResponse => {
        const checkoutUrl = prepareCheckoutResponse && prepareCheckoutResponse.checkoutUrl;
        if (checkoutUrl && window && window.location) {
          window.location.href = checkoutUrl;
        }

        analytics.trackEvent(
          `MultiProp.Checkout.${multiItem.cartInfo.type}.${multiItem.itemCount}`,
          'number of properties in cart and business model',
          analytics.omnitureData,
          null,
          true
        );
      })
      .catch(() => {
        if (analytics) {
          analytics.trackEvent(
            `MultiProp.Checkout.Failure.${multiItem.itemCount}`,
            'Prepare checkout failure',
            analytics.omnitureData,
            null,
            true
          );
        }
      });
  }

  public getCartItems(): Promise<InCartProperty[]> {
    const { multiItem, context } = this.props;

    return multiItem.getCartDetails(context);
  }

  public cartItemDeleter(id) {
    const { multiItem, context, analytics } = this.props;

    return (e?: SyntheticEvent) => {
      if (e && typeof e.stopPropagation === 'function') {
        e.stopPropagation();
      }
      multiItem.removeItemFromCart(context, id, analytics).then(() => {
        if (analytics) {
          analytics.trackEvent('MultiProp.RemoveProperty', 'MultiProp Property Removed', analytics.omnitureData, null, true);
        }
      });
    };
  }

  public onToastDismiss() {
    const { multiItem } = this.props;
    multiItem.resetStatus();
  }

  public render() {
    const { l10n, multiItem, routeStore } = this.props;
    if (!multiItem || multiItem.itemCount === 0) {
      return null;
    }

    const holdingAreaDialogQueryBase = { pwaDialog: 'multiItemDialog' };
    const dialogTo = {
      pathname: routeStore.pathname,
      search: routeStore.mergeQueryString && routeStore.mergeQueryString(routeStore.search, holdingAreaDialogQueryBase)
    };

    const toggleRef = toggleDialogRef => {
      this.toggleDialogRef = toggleDialogRef;
    };

    const titleText = l10n.formatText(
      multiItem.itemCount === 1 ? 'multiProperty.playback.titleSingle' : 'multiProperty.playback.titleMultiple',
      multiItem.itemCount
    );

    return (
      <React.Fragment>
        <ItemsPlayback titleText={titleText} setToggleRef={toggleRef} linkTo={dialogTo} itemCount={multiItem.itemCount} />
        <UitkPageLoader visible={multiItem.status === MultiItemStoreStatus.ADDING_PROPERTY} />
        <SearchCriteriaContext.Consumer>
          {(searchCriteria: SearchCriteriaModel) => (
            <QueryRoute query={holdingAreaDialogQueryBase}>
              <Layer id="multi-item-layer">
                <HoldingAreaContent
                  cartItemDeleter={this.cartItemDeleter}
                  closeDialog={this.closeDialog}
                  getCartItems={this.getCartItems}
                  goToCheckout={this.goToCheckout}
                  l10n={l10n}
                  searchCriteria={searchCriteria}
                  isAllSoldOut={multiItem && multiItem.isAllSoldOut}
                />
              </Layer>
            </QueryRoute>
          )}
        </SearchCriteriaContext.Consumer>
        <UitkToast onDismiss={this.onToastDismiss} show={multiItem && multiItem.status === MultiItemStoreStatus.ADDING_PROPERTY_FAILURE}>
          {multiItem && multiItem.error && multiItem.error.message}
        </UitkToast>
      </React.Fragment>
    );
  }
}
